import {Award, Folder, MapPin, Settings as SettingsIcon, UserCircle} from "lucide-react";
import {ContactInfo} from './ContactInfo.tsx';
import {CountyCoverageArea} from './CountryCoverageArea.tsx';
import {ErrorsAndOmissions} from './ErrorsAndOmissions.tsx';
import {LicenseAndCerts} from "./LicenseAndCerts.tsx";
import {Documents} from "./Documents.tsx";
import {Settings} from "./Settings.tsx";
import {AppraiserInfo, DEFAULT_APPRAISER} from "../../../api/ApiTypes.ts";
import React, {useEffect, useMemo, useState} from "react";
import {useParams} from "react-router-dom";
import {useAppraiserApi} from "../../../context/ApiContext.tsx";
import {SamTabedPage} from "../../../components/SamTabedPaged.tsx";
import {AppraiserProfileSkeleton} from "./AppraiserProfileSkeleton.tsx";


export interface AppraiserProfileTabProps {
    appraiserInfo: AppraiserInfo;
    refreshAppraiserData: () => Promise<void>;
}


export const AppraiserProfile: React.FC = () => {
    const { securityUserId, tabName } = useParams(); // Get the ID and tab name from URL parameters
    const appraiserApi = useAppraiserApi();

    const [appraiserLoading, setAppraiserLoading] = useState(true); // Start with loading true
    const [appraiserInfo, setAppraiserInfo] = useState<AppraiserInfo>(DEFAULT_APPRAISER);
    const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0); // State to manage the selected tab index

    const refreshAppraiserData = async () => {
        setAppraiserLoading(true); // Set loading to true before fetching
        try {
            // Determine which API method to call based on the 'forMe' flag
            const appraiser = securityUserId === "me"
                ? await appraiserApi.getMyAppraiserInfo()
                : await appraiserApi.getAppraiserInfo(parseInt(securityUserId!));

            setAppraiserInfo(appraiser);
        } catch (error) {
            console.error('Failed to fetch appraiser data:', error);
        } finally {
            setAppraiserLoading(false);
        }
    };

    useEffect(() => {
        refreshAppraiserData();
    },[]);

    const tabComponents = useMemo(() => {
        return [
            {
                name: 'Personal Info',
                icon: <UserCircle className="h-5 w-5"/>,
                slug: 'personal-info',
                description: 'If you need to change any of the information listed below, please contact valuationvm@stewart.com',
                component: () =>
                    <ContactInfo
                        appraiserInfo={appraiserInfo}
                        refreshAppraiserData={refreshAppraiserData}
                    />
            },
            {
                name: 'Coverage Area',
                icon: <MapPin className="h-5 w-5"/>,
                slug: 'coverage-area',
                description: 'Set your county coverage area and service regions',
                component: () =>
                    <CountyCoverageArea
                        appraiserInfo={appraiserInfo}
                        refreshAppraiserData={refreshAppraiserData}
                    />

            },
            {
                name: 'Certifications',
                icon: <Award className="h-5 w-5"/>,
                slug: 'certifications',
                description: 'Manage your licenses, certifications and E&O insurance',
                component: () => (
                    <div>
                        <ErrorsAndOmissions
                            appraiserInfo={appraiserInfo}
                            refreshAppraiserData={refreshAppraiserData}
                        />
                        <div className="mt-8">
                            <LicenseAndCerts
                                appraiserInfo={appraiserInfo}
                                refreshAppraiserData={refreshAppraiserData}
                            />
                        </div>
                    </div>
                )
            },
            {
                name: 'Documents',
                icon: <Folder className="h-5 w-5"/>,
                slug: 'documents',
                description: 'Access and manage your important documents',
                component: () =>
                    <Documents
                        appraiserInfo={appraiserInfo}
                        refreshAppraiserData={refreshAppraiserData}
                    />
            },
            {
                name: 'Settings',
                slug: 'settings',
                icon: <SettingsIcon className="h-5 w-5"/>,
                description: 'Manage your account settings and security preferences',
                component: () =>
                    <Settings
                        appraiserInfo={appraiserInfo}
                        refreshAppraiserData={refreshAppraiserData}
                    />
            },
        ]
    }, [appraiserInfo]);

    useEffect(() => {
        const determineTabIndex = () => {
            if (!tabName) {
                return;
            }
            const index = tabComponents.findIndex(tab => tab.slug === tabName.toLowerCase());
            if (index !== -1) {
                setSelectedTabIndex(index);
            } else {
                setSelectedTabIndex(0);
            }
        };

        determineTabIndex(); // Actually call the function
    }, [tabName, tabComponents]);

    if (!appraiserInfo) {
        return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mt-4">
                <h2 className="text-yellow-700 font-medium">Appraiser Not Found</h2>
                <p className="text-yellow-600 mt-1"> The requested appraiser could not be found.</p>
            </div>
        );
    }

    return (

        <>
            {appraiserLoading ?
                ( <AppraiserProfileSkeleton/> ) :
                ( // Main container with responsive max-width and horizontal/vertical padding
                <div className="max-w-7xl mx-auto px-4 py-8">
                    {/* Header section with title and description */}
                    <div className="mb-8">
                        {/* Large, bold title text in dark gray */}
                        <h1 className="text-2xl font-bold text-slate-800 mb-4">Profile Settings</h1>
                        {/* Subheading with medium gray text */}
                        <p className="text-slate-600">Manage your account settings and preferences</p>
                    </div>

                    <SamTabedPage
                        tabs={tabComponents}
                        selectedTabIndex={selectedTabIndex}
                        setSelectedTabIndex={setSelectedTabIndex}
                    />
                </div>)
            }
        </>
    );
};